import { AxiosError } from 'axios'
import produce from 'immer'
import React from 'react'
import { QueryStatus } from 'react-query'
import { UseInfiniteQueryResult } from 'react-query/types/react/types'
import create from 'zustand'

import { PagedProcessResponse } from 'types/api/services/dependencies/dependency'
import { Process } from 'types/api/services/service'

import { useGremlinPagination } from './use-gremlin-query'

export interface ProcessViewState {
  status: QueryStatus
  processes: {
    [id: string]: Process
  }
  hasNextPage: boolean
  set: (fn: (state: ProcessViewState) => void) => void
  clearAll: () => void
}

export const useProcessViewStore = create<ProcessViewState>((set) => ({
  status: 'loading',
  processes: {},
  hasNextPage: true,
  set: (fn) => set(produce(fn)),
  clearAll: () =>
    set(
      produce((state) => {
        state.status = 'loading'
        state.processes = {}
      })
    )
}))

export function useProcessView({
  enabled = false,
  endpoint
}): UseInfiniteQueryResult<PagedProcessResponse, AxiosError> {
  const set = useProcessViewStore((state) => state.set)

  const pagedAllFetchState = useGremlinPagination<PagedProcessResponse>(
    endpoint,
    {},
    {
      onSuccess: (data) => {
        set((state) => {
          const all = data?.pages?.flatMap((page) => page.items)

          state.processes = {
            ...Object.fromEntries(all.map((s) => [s.id, s]))
          }
        })
      },
      onSettled: (data, error) => {
        set((state) => {
          state.status =
            error && (!data || typeof data === 'string') ? 'error' : 'success'
        })
      },
      enabled
    }
  )

  React.useEffect(() => {
    let timeout
    if (
      pagedAllFetchState.hasNextPage &&
      !pagedAllFetchState.isFetchingNextPage
    ) {
      set((state) => {
        state.hasNextPage = true
      })
      timeout = setTimeout(() => {
        pagedAllFetchState.fetchNextPage()
      }, 200)
    } else if (!pagedAllFetchState.hasNextPage) {
      set((state) => {
        state.hasNextPage = false
      })
    }
    return () => clearTimeout(timeout)
  }, [pagedAllFetchState, set])

  return pagedAllFetchState
}
